import { dashboardApi } from '@/store/api/base/api.base';
import { expenseQueryParams, expenseReviewParams, expenseSummaryParams } from '@/function/expense/expense.query.params';
import { dropExpense, patchExpense, writeExpense } from '@/store/api/expense/expense.cache';
import type { TypeExpense, TypeExpenseInput, TypeExpenseMethod } from '@/types/expense/types.expense';
import type { TypeExpensePage, TypeExpenseQuery, TypeExpenseSupplier, TypeExpenseTotals } from '@/types/expense/types.expense.query';
import type { TypeExpenseReview, TypeExpenseSummary } from '@/types/expense/types.expense.summary';
import type { TypeBulkSkip } from '@/types/list/types.list.bulk';

type TypeExpenseBulkPaymentResult = {
    updated: TypeExpense[];
    unchanged: string[];
    skipped: TypeBulkSkip[];
};

type TypeExpenseBulkRemoveResult = {
    removed: string[];
    skipped: TypeBulkSkip[];
};

type TypeExpenseBacklogResult = {
    source: TypeExpense;
    created: TypeExpense[];
    stopped: { date: string; message: string } | null;
};

const PAGE_SIZE = 50;

const toExpenseInput = (expense: TypeExpense | TypeExpenseInput): TypeExpenseInput => {
    const { id, number, invoiceId, rebilledAt, recordedAt, createdAt, updatedAt, attachments, renewedBy, ...input } = expense as TypeExpense;
    return input;
};

export const expenseApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        listExpensesPage: build.infiniteQuery<TypeExpensePage, TypeExpenseQuery, string | null>({
            infiniteQueryOptions: {
                initialPageParam: null,
                getNextPageParam: (last) => (last.page?.hasMore && last.page.nextCursor ? last.page.nextCursor : undefined),
            },
            query: ({ queryArg, pageParam }) => ({ url: '/expenses', params: { ...expenseQueryParams(queryArg), limit: PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) } }),
            providesTags: ['Expense'],
        }),

        readExpenseTotals: build.query<TypeExpenseTotals, TypeExpenseQuery>({
            query: (queryArg) => ({ url: '/expenses', params: { ...expenseQueryParams(queryArg), limit: 1, totals: true } }),
            transformResponse: (page: TypeExpensePage) => page.totals ?? { count: 0, totalCents: 0, openCents: 0, paid: 0, due: 0, overdue: 0 },
            providesTags: ['ExpenseTotals'],
        }),

        readExpenseSummary: build.query<TypeExpenseSummary, string>({
            query: (today) => ({ url: '/expenses/summary', params: expenseSummaryParams(today) }),
            providesTags: ['ExpenseSummary'],
        }),

        readExpenseReview: build.query<TypeExpenseReview, string>({
            query: (today) => ({ url: '/expenses/review', params: expenseReviewParams(today) }),
            providesTags: ['ExpenseReview'],
        }),

        listExpenseSuppliers: build.query<TypeExpenseSupplier[], string>({
            query: (search) => ({ url: '/expenses/suppliers', params: { search, limit: 200 } }),
            providesTags: ['ExpenseSummary'],
        }),

        readExpenseTwins: build.query<TypeExpense[], { documentDate: string; totalCents: number }>({
            query: ({ documentDate, totalCents }) => ({ url: '/expenses/twins', params: { documentDate, totalCents } }),
            providesTags: ['ExpenseReview'],
        }),

        readExpense: build.query<TypeExpense, string>({
            query: (id) => ({ url: `/expenses/${id}` }),
            providesTags: (_result, _error, id) => [{ type: 'Expense', id }],
        }),

        createExpense: build.mutation<TypeExpense, { expense: TypeExpenseInput; clientRef: string }>({
            query: ({ expense, clientRef }) => ({ url: '/expenses', method: 'post', data: { expense: toExpenseInput(expense), clientRef } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeExpense(dispatch, data, { before: null });
                } catch {
                }
            },
        }),

        updateExpense: build.mutation<TypeExpense, { id: string; expense: TypeExpenseInput }>({
            query: ({ id, expense }) => ({ url: `/expenses/${id}`, method: 'patch', data: { expense: toExpenseInput(expense) } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeExpense(dispatch, data);
                } catch {
                }
            },
        }),

        recordExpense: build.mutation<TypeExpense, { id: string; expense: TypeExpenseInput }>({
            query: ({ id, expense }) => ({ url: `/expenses/${id}/record`, method: 'post', data: { expense: { ...toExpenseInput(expense), stage: 'recorded', plannedDate: '' } } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeExpense(dispatch, data);
                } catch {
                }
            },
        }),

        setExpensePayment: build.mutation<TypeExpense, { id: string; paidDate: string; method: TypeExpenseMethod }>({
            query: ({ id, paidDate, method }) => ({ url: `/expenses/${id}/payment`, method: 'patch', data: { paidDate, method } }),
            onQueryStarted: async ({ id, paidDate, method }, { dispatch, queryFulfilled }) => {
                const undo = patchExpense(dispatch, id, { paidDate, method });
                try {
                    const { data } = await queryFulfilled;
                    writeExpense(dispatch, data);
                } catch {
                    undo();
                }
            },
        }),

        setExpensePaymentMany: build.mutation<TypeExpenseBulkPaymentResult, { ids: string[]; paidDate: string; method: TypeExpenseMethod | '' }>({
            query: ({ ids, paidDate, method }) => ({ url: '/expenses/payment', method: 'patch', data: { ids, paidDate, method } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    for (const expense of data.updated) writeExpense(dispatch, expense);
                } catch {
                }
            },
        }),

        removeExpenseMany: build.mutation<TypeExpenseBulkRemoveResult, string[]>({
            query: (ids) => ({ url: '/expenses', method: 'delete', data: { ids } }),
            onQueryStarted: async (_ids, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    for (const id of data.removed) dropExpense(dispatch, id);
                } catch {
                }
            },
        }),

        rebillExpense: build.mutation<TypeExpense, { id: string; invoiceId: string }>({
            query: ({ id, invoiceId }) => ({ url: `/expenses/${id}/rebill`, method: 'patch', data: { invoiceId } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeExpense(dispatch, data);
                } catch {
                }
            },
        }),

        duplicateExpense: build.mutation<TypeExpense, { id: string; documentDate?: string; clientRef: string }>({
            query: ({ id, documentDate, clientRef }) => ({ url: `/expenses/${id}/duplicate`, method: 'post', data: { documentDate: documentDate ?? '', clientRef } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeExpense(dispatch, data, { before: null });
                } catch {
                }
            },
        }),

        renewExpenseBacklog: build.mutation<TypeExpenseBacklogResult, { id: string; dates: string[]; clientRef: string }>({
            query: ({ id, dates, clientRef }) => ({ url: `/expenses/${id}/renewals`, method: 'post', data: { dates, clientRef } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeExpense(dispatch, data.source);
                    for (const expense of data.created) writeExpense(dispatch, expense, { before: null });
                } catch {
                }
            },
        }),

        stopExpenseRenewal: build.mutation<TypeExpense[], { id: string; stopDate: string }>({
            query: ({ id, stopDate }) => ({ url: `/expenses/${id}/stop`, method: 'patch', data: { stopDate } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    for (const expense of data) writeExpense(dispatch, expense);
                } catch {
                }
            },
        }),

        removeExpense: build.mutation<null, string>({
            query: (id) => ({ url: `/expenses/${id}`, method: 'delete' }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    dropExpense(dispatch, id);
                } catch {
                }
            },
        }),
    }),
});

export const {
    useListExpensesPageInfiniteQuery,
    useReadExpenseTotalsQuery,
    useReadExpenseSummaryQuery,
    useReadExpenseReviewQuery,
    useListExpenseSuppliersQuery,
    useReadExpenseTwinsQuery,
    useReadExpenseQuery,
    useCreateExpenseMutation,
    useUpdateExpenseMutation,
    useRecordExpenseMutation,
    useSetExpensePaymentMutation,
    useSetExpensePaymentManyMutation,
    useRemoveExpenseManyMutation,
    useRebillExpenseMutation,
    useDuplicateExpenseMutation,
    useRenewExpenseBacklogMutation,
    useStopExpenseRenewalMutation,
    useRemoveExpenseMutation,
} = expenseApi;

export default expenseApi;
